import React, { useState } from 'react';

import { routes } from '@aline/routes';
import { Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Apollo } from 'src/gql/generated';
import { useSelectCurrentUser } from 'src/gql/hooks/user';
import { Flag } from 'src/helpers/feature-flags';
import { makeStyles } from 'tss-react/mui';

import { DemographicInfo } from './DemographicInfo';
import { SidePanel } from './SidePanel';
import { TeamSetup } from './TeamSetup';
import { useCreateOrUpdateOrg } from '../admin/org/hooks';

const useStyles = makeStyles()(theme => ({
  buttons: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  content: {
    alignItems: 'center',
    background: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    boxSizing: 'border-box',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    overflow: 'auto',
    padding: theme.spacing(6, 6, 2, 6),
  },
  contentInner: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    maxWidth: 600,
    textAlign: 'center',
    width: '100%',
  },
  root: {
    background: '#F5F5F5',
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(2),
    height: '100vh',
    padding: theme.spacing(2),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
  },
  title: {
    marginBottom: theme.spacing(1),
  },
}));

const steps = [
  {
    component: DemographicInfo,
    description: 'Provide info about you and your organization.',
    id: 'demographic',
    title: 'Basic information',
  },
  {
    component: TeamSetup,
    description: 'Start collaborating with your team.',
    id: 'team',
    title: 'Create your team',
  },
];

interface Step1Info {
  firstName: string;
  lastName: string;
  size: Apollo.OrgSize;
  stepIndex: 0;
  type: Apollo.OrgType;
  website: string;
}

interface Step2Info {
  existingOrgId: UUID | null;
  orgName: string;
  stepIndex: 1;
  teamMembers: string[];
}

type PreOnboardingInfo = Step1Info | Step2Info;

interface StepContentProps {
  currentStep: number;
  currentUser: ReturnType<typeof useSelectCurrentUser>;
  handleBack: () => void;
  onContinue: OnContinueFunction;
}

function StepContent({ currentStep, currentUser, handleBack, onContinue }: StepContentProps) {
  switch (currentStep) {
    case 0:
      return <DemographicInfo currentUser={currentUser} onContinue={onContinue} />;
    case 1:
      return <TeamSetup currentUser={currentUser} onBack={handleBack} onContinue={onContinue} />;
    default:
      return <DemographicInfo currentUser={currentUser} onContinue={onContinue} />;
  }
}

export type OnContinueFunction = (data: PreOnboardingInfo) => Promise<void>;
interface DemographicInfoType {
  size?: Apollo.OrgSize;
  type?: Apollo.OrgType;
  website?: string;
}

export function PreOnboardingContainer() {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const navigate = useNavigate();
  const [updateUser] = Apollo.useUpdateUserMutation();
  const { createOrg, updateOrg } = useCreateOrUpdateOrg();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [demographicInfo, setDemographicInfo] = useState<DemographicInfoType>({});

  const handleBack = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const handleContinue = async (data: PreOnboardingInfo) => {
    switch (data.stepIndex) {
      case 0:
        setDemographicInfo({
          size: data.size,
          type: data.type,
          website: data.website,
        });
        if (currentUser?.id) {
          await updateUser({
            variables: {
              userId: currentUser.id,
              userProps: { firstName: data.firstName, lastName: data.lastName },
            },
          });
        }

        setCurrentStep(prev => prev + 1);
        break;

      case 1:
        if (data.existingOrgId) {
          await updateOrg(data.existingOrgId, {
            dispatchSnack: false,
            newMemberEmails: data.teamMembers,
            props: {
              isPreOnboardingCompleted: true,
              name: data.orgName,
              size: demographicInfo.size,
              type: demographicInfo.type,
              website: demographicInfo.website,
            },
          });
        } else {
          await createOrg({
            dispatchSnack: false,
            props: {
              isPreOnboardingCompleted: true,
              newMemberEmails: data.teamMembers,
              orgName: data.orgName,
              size: demographicInfo.size,
              type: demographicInfo.type,
              website: demographicInfo.website,
            },
          });
        }

        navigate(Flag.GET_STARTED_ONBOARDING ? routes.GetStarted : routes.ChatIndex);
        break;
    }
  };

  return (
    <div className={classes.root}>
      <SidePanel currentStep={currentStep} />
      <Paper className={classes.content}>
        <div className={classes.contentInner}>
          <Typography align="center" className={classes.title} variant="h1">
            {steps[currentStep].title}
          </Typography>
          <Typography align="center" className={classes.subtitle} variant="body1">
            {steps[currentStep].description}
          </Typography>
          <StepContent
            currentStep={currentStep}
            currentUser={currentUser}
            handleBack={handleBack}
            onContinue={handleContinue}
          />
        </div>
      </Paper>
    </div>
  );
}
